/**
 * Multi-storage persistence for auth credentials. Targets browsers with
 * varying privacy postures (Chrome default, Brave with strict Shields,
 * Safari with ITP, Firefox total cookie protection, incognito modes).
 *
 * Strategy:
 *   - **Authoritative copy**: in-memory (held by Zustand store).
 *   - **Persisted sidecars** written best-effort to localStorage, sessionStorage,
 *     and a same-origin cookie. Any one surviving keeps the user logged in
 *     across reloads.
 *   - On boot, read order: localStorage → sessionStorage → cookie. First hit wins.
 *   - If all writes fail, the session is memory-only (works within tab, lost
 *     on reload). We surface a console.warn for visibility.
 */

const STORAGE_KEY = "kormax-auth-creds";
const COOKIE_KEY = "kormax-auth-creds";
const COOKIE_MAX_AGE_S = 30 * 24 * 60 * 60; // 30 days

export interface PersistedCredentials {
  userName: string;
  token: string;
}

function debug(...args: unknown[]) {
  console.warn("[KORMAX-AUTH]", "[storage]", ...args);
}

function safeGetStorage(kind: "local" | "session"): Storage | null {
  try {
    const s = kind === "local" ? window.localStorage : window.sessionStorage;
    // Probe — Safari/Brave private mode can throw on writes despite the
    // object being present.
    const probe = "__kormax_probe__";
    s.setItem(probe, "1");
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

const ls: Storage | null = safeGetStorage("local");
const ss: Storage | null = safeGetStorage("session");

function tryRead(s: Storage | null): string | null {
  if (!s) return null;
  try {
    return s.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function tryWrite(s: Storage | null, value: string): boolean {
  if (!s) return false;
  try {
    s.setItem(STORAGE_KEY, value);
    return true;
  } catch {
    return false;
  }
}

function tryRemove(s: Storage | null): void {
  if (!s) return;
  try {
    s.removeItem(STORAGE_KEY);
  } catch {
    /* swallow */
  }
}

function readCookie(): string | null {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_KEY}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(value: string): boolean {
  try {
    const enc = encodeURIComponent(value);
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${COOKIE_KEY}=${enc}; max-age=${COOKIE_MAX_AGE_S}; path=/; SameSite=Lax${secure}`;
    // Verify it actually stuck — Brave can silently drop writes.
    return readCookie() !== null;
  } catch {
    return false;
  }
}

function removeCookie(): void {
  try {
    document.cookie = `${COOKIE_KEY}=; max-age=0; path=/`;
  } catch {
    /* swallow */
  }
}

function parse(raw: string | null): PersistedCredentials | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<PersistedCredentials>;
    if (typeof p?.userName === "string" && typeof p?.token === "string" && p.userName && p.token) {
      return { userName: p.userName, token: p.token };
    }
  } catch {
    /* swallow */
  }
  return null;
}

export function readPersistedCredentials(): PersistedCredentials | null {
  return (
    parse(tryRead(ls)) ??
    parse(tryRead(ss)) ??
    parse(readCookie())
  );
}

export function persistCredentials(creds: PersistedCredentials): void {
  const json = JSON.stringify(creds);
  const okLS = tryWrite(ls, json);
  const okSS = tryWrite(ss, json);
  const okCookie = writeCookie(json);
  if (!okLS && !okSS && !okCookie) {
    debug(
      "ALL persistent stores blocked (LS, SS, cookie). Session is memory-only — close-tab will sign out.",
    );
  } else if (!okLS) {
    debug("localStorage unavailable; using fallback sidecar", { sessionStorage: okSS, cookie: okCookie });
  }
}

export function clearPersistedCredentials(): void {
  tryRemove(ls);
  tryRemove(ss);
  removeCookie();
}

export function describeStorageEnv(): Record<string, boolean> {
  return {
    localStorage: ls !== null,
    sessionStorage: ss !== null,
    cookie: (() => {
      const probe = "__kormax_cookie_probe__";
      try {
        document.cookie = `${probe}=1; path=/; SameSite=Lax`;
        const ok = document.cookie.includes(probe);
        document.cookie = `${probe}=; max-age=0; path=/`;
        return ok;
      } catch {
        return false;
      }
    })(),
  };
}
